// src/App-Cognito.jsx
// Updated App component to use AWS Cognito instead of Auth0

import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { createShieldShotTheme } from "./theme";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// Components
import Home from "./components/home";
import About from "./components/pages/About";
import Pricing from "./components/pages/Pricing";
import Roadmap from "./components/pages/Roadmap";
import Tiers from "./components/tiers/tiers";
import NavBar from "./components/parts/navBar";
import Updates from "./components/parts/updates";
import Settings from "./components/settings/settings";
import Activity from "./components/activity/activity";
import EmailList from "./components/emailList/emailList";
import NotFound from "./components/notFound";
import Dashboard from "./components/dashboard";
import Collections from "./components/collections/collectionsV2";
import LoadingScreen from "./components/parts/loadingScreen";
import CollectionDetails from "./components/collections/collectionDetails";
import CollectionAdd from "./components/collections/collectionAdd";
import TierAddWrapper from "./components/tiers/tierAdd";
import TierDetailsWrapper from "./components/tiers/tierDetails";
import DistributionWrapper from "./components/distribution/distribution";
import DistributionAdd from "./components/distribution/distributionAdd";
import DistributionDetailsWrapper from "./components/distribution/distributionDetails";
import Feedback from "./components/parts/feedback";
import Faq from "./components/parts/faq";
import Store from "./components/store/store";
import LinkedAccounts from "./components/linkedAccounts/LinkedAccounts";
import CognitoLoginForm from "./components/auth/CognitoLoginForm";
import AuthTest from "./components/auth/AuthTest";
import PrivateRoute from "./components/PrivateRoute";
import { useCognitoAuth } from "./aws-amplify-auth";
import DashboardCenter from "./components/dashboard/dashboardCenter";
import AdminLayout from "./components/admin/adminLayout";
import Subscriptions from "./components/subscriptions";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import SubscriptionCancel from "./components/SubscriptionCancel";
import GettingStarted from "./components/parts/gettingStarted";
import Footer from "./components/footer";
import Register from "./components/register";
import ErrorBoundary from "./components/ErrorBoundary";
import SharePage from "./components/share/SharePage";

import ThemeModeProvider from './themeMode';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useIdleTimer } from 'react-idle-timer';


function App() {
  const cognitoAuth = useCognitoAuth();
  const {
    isAuthenticated,
    loading,
    tokenLoading,
    profileLoading,
    user,
    AWS,
    s3Client,
    active,
    isRegistered,
    adminMode,
    currentStorage,
    maxStorage,
    isAdmin,
    showFooter,
    token,
    signOut,
    loginWithRedirect,
    refreshAuth
  } = cognitoAuth;

  const accountType = user?.app_metadata?.accountType;

  // Redirect helper: uploader accounts should not access distributor-management routes
  const UploaderRedirect = ({ children }) => {
    if (accountType === 'uploader') {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // --- Idle/Active Notification Polling and WebSocket Management ---
  // We'll use a ref to NavBar to call its methods
  const navBarRef = React.useRef();

  // Idle timer configuration - 15 minute warning system
  const handleOnIdle = () => {
    if (isAuthenticated) {
      toast.warning("You've been idle for 15 minutes. Your session may expire soon. Move your mouse or click to stay active.", {
        autoClose: 10000,
        toastId: 'idle-warning' // Prevent duplicate toasts
      });
      // Stop polling and disconnect WebSocket
      if (navBarRef.current && typeof navBarRef.current.cleanupNotifications === 'function') {
        navBarRef.current.cleanupNotifications();
      }
    }
  };

  const handleOnActive = async () => {
    // User became active after being idle
    console.log('User became active after idle period');
    if (isAuthenticated && typeof refreshAuth === 'function') {
      try {
        await refreshAuth();
        // After refresh, reconnect WebSocket/polling
        if (navBarRef.current && typeof navBarRef.current.initializeNotifications === 'function') {
          navBarRef.current.initializeNotifications();
        }
      } catch (error) {
        console.warn('Failed to refresh auth on activity:', error);
      }
    }
  };

  const { getRemainingTime, isIdle } = useIdleTimer({
    timeout: 1000 * 60 * 15, // 15 minutes
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    debounce: 500,
    events: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'],
    startManually: false,
    crossTab: true
  });

  if (loading || tokenLoading || profileLoading) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeModeProvider>
          <Router>
            <ToastContainer 
              position="top-right" 
              toastClassName={(context) => {
                const type = context?.type || 'default';
                return `Toastify__toast Toastify__toast--${type}`;
              }}
              bodyClassName={(context) => {
                const type = context?.type || 'default';
                return `Toastify__toast-body`;
              }}
              closeButton={(props) => (
                <button 
                  {...props} 
                  data-testid="toast-close"
                  className="Toastify__close-button"
                >
                  ×
                </button>
              )}
              data-testid="toast-container"
            />
            {!isAuthenticated ? (
              <ThemeProvider theme={createShieldShotTheme('light')}>
                <CssBaseline />
                <div className="App">
                  <div className="content-wrapper">
                    <Routes>
                      <Route
                        exact
                        path="/"
                        element={
                          <Home
                            isAuthenticated={isAuthenticated}
                            AWS={s3Client}
                          />
                        }
                      />
                      <Route
                        path="/about"
                        element={<About />}
                      />
                      <Route
                        path="/pricing"
                        element={<Pricing />}
                      />
                      <Route
                        path="/roadmap"
                        element={<Roadmap />}
                      />
                      <Route path="/share/:id" element={<SharePage />} />
                      <Route path="/login" element={<CognitoLoginForm />} />
                      <Route path="/auth-test" element={<AuthTest />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </div>
                </div>
              </ThemeProvider>
            ) : adminMode ? (
              <React.Fragment>
                <div className="App">
                  <div className="content-wrapper h-100 min100vh">
                    <AdminLayout {...cognitoAuth} token={token} AWS={s3Client} />
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div className="App App-with-footer">
                  <div className="content-wrapper">
                    <NavBar
                      ref={navBarRef}
                      {...cognitoAuth}
                      user={user}
                      accountType={accountType}
                      active={active}
                      isRegistered={isRegistered}
                      refreshAuth={refreshAuth}
                      AWS={s3Client}
                      currentStorage={currentStorage}
                      maxStorage={maxStorage}
                    />

                    <Routes>
                      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/subscriptions" element={<PrivateRoute><Subscriptions {...cognitoAuth} token={token} user={user} AWS={s3Client} /></PrivateRoute>} />
                      <Route path="/success" element={<PrivateRoute><SubscriptionSuccess /></PrivateRoute>} />
                      <Route path="/cancel" element={<PrivateRoute><SubscriptionCancel /></PrivateRoute>} />
                      <Route path="/feedback" element={<PrivateRoute><Feedback {...cognitoAuth} token={token} AWS={s3Client} /></PrivateRoute>} />
                      <Route path="/register" element={<PrivateRoute><Register {...cognitoAuth} token={token} isRegistered={isRegistered} AWS={s3Client} /></PrivateRoute>} />
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={
                        <PrivateRoute>
                          <Dashboard {...cognitoAuth} currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <DashboardCenter
                              {...cognitoAuth}
                              isAdmin={isAdmin}
                              AWS={s3Client}
                              user={user}
                              token={token}
                            />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="/getting-started" element={
                        <PrivateRoute>
                          <Dashboard {...cognitoAuth} currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <GettingStarted {...cognitoAuth} />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="/activities" element={
                        <PrivateRoute>
                          <Dashboard {...cognitoAuth} currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <Activity {...cognitoAuth} token={token} AWS={s3Client} />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="/collections" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <Collections {...cognitoAuth} token={token} user={user} AWS={s3Client} />
                          </Dashboard>
                        </PrivateRoute>
                      } />
                      <Route path="/collections/:id" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <CollectionDetails {...cognitoAuth} user={user} token={token} active={active} AWS={s3Client} />
                          </Dashboard>
                        </PrivateRoute>
                      } />
                      <Route path="/collections/add" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <CollectionAdd {...cognitoAuth} token={token} AWS={s3Client} />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="/tiers" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <Tiers {...cognitoAuth} token={token} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />
                      <Route path="/tiers/add" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <TierAddWrapper {...cognitoAuth} user={user} token={token} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />
                      <Route path="/tiers/:id" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <TierDetailsWrapper {...cognitoAuth} user={user} token={token} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />
                      <Route path="/activity" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <Activity {...cognitoAuth} token={token} AWS={s3Client} />
                          </Dashboard>
                        </PrivateRoute>
                      } />
                      <Route path="/emails" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <EmailList {...cognitoAuth} token={token} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />

                      <Route path="/distributions" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <DistributionWrapper {...cognitoAuth} token={token} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />
                      <Route path="/distributions/add" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <DistributionAdd {...cognitoAuth} token={token} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />
                      <Route path="/distributions/:id" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <DistributionDetailsWrapper {...cognitoAuth} token={token} active={active} user={user} AWS={s3Client} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />
                      <Route path="/settings/*" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <Settings {...cognitoAuth} AWS={s3Client} user={user} token={token} />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="/store" element={
                        <PrivateRoute>
                          <UploaderRedirect>
                            <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                              <Store token={token} />
                            </Dashboard>
                          </UploaderRedirect>
                        </PrivateRoute>
                      } />

                      <Route path="/linked-accounts" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <LinkedAccounts {...cognitoAuth} token={token} user={user} AWS={s3Client} />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="/faq" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <Faq />
                          </Dashboard>
                        </PrivateRoute>
                      } />
                      <Route path="/updates" element={
                        <PrivateRoute>
                          <Dashboard currentStorage={currentStorage} maxStorage={maxStorage} AWS={s3Client} accountType={accountType}>
                            <Updates />
                          </Dashboard>
                        </PrivateRoute>
                      } />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </div>
                  <Footer showFooter={true} />
                </div>
              </React.Fragment>
            )}
          </Router>
        </ThemeModeProvider>
      </LocalizationProvider>
    </ErrorBoundary>
  );
}

export default App;
